import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTournament } from "../contexts/TournamentContext";
import { useSoundEffect } from "../hooks/useSound";
import { soundManager, SOUNDS } from "../utils/soundManager";
import { Token } from "../types/tournament";

interface VersusScreenProps {
  isVisible: boolean;
  onComplete: () => void;
}

interface TokenSideProps {
  token: Token;
  side: "left" | "right";
  delay?: number;
}

const TokenSide: React.FC<TokenSideProps> = ({ token, side, delay = 1 }) => {
  const isLeft = side === "left";
  const themeColor = isLeft
    ? { rgb: "251, 191, 36", name: "amber" } // Gold/amber on left
    : { rgb: "168, 85, 247", name: "purple" }; // Purple on right

  return (
    <motion.div
      className="relative w-full h-1/2 sm:w-1/2 sm:h-full overflow-hidden"
      initial={{
        x: window.innerWidth < 640 ? 0 : isLeft ? "-100%" : "100%",
        y: window.innerWidth < 640 ? (isLeft ? "-100%" : "100%") : 0,
      }}
      animate={{ x: 0, y: 0 }}
      exit={{
        x: window.innerWidth < 640 ? 0 : isLeft ? "-100%" : "100%",
        y: window.innerWidth < 640 ? (isLeft ? "-100%" : "100%") : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 130,
        damping: 20,
        duration: 0.5,
      }}
    >
      {/* Background image with Ken Burns effect */}
      <motion.div
        className="absolute inset-0"
        animate={{
          scale: [1, 1.1, 1],
          x: isLeft ? [0, -10, 0] : [0, 10, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <img
          src={`/tokens/images/${token.imagePath}`}
          alt={token.name}
          className="w-full h-full object-cover"
          style={{
            filter: "brightness(0.7) contrast(1.2)",
          }}
        />
      </motion.div>

      {/* Gradient overlay - darker at edges, transparent in middle */}
      <div className="absolute inset-0">
        {/* Top gradient */}
        <div
          className="absolute top-0 left-0 right-0 h-1/3"
          style={{
            background: `linear-gradient(to bottom, rgba(0, 0, 0, 0.7), transparent)`,
          }}
        />
        {/* Bottom gradient */}
        <div
          className="absolute bottom-0 left-0 right-0 h-1/3"
          style={{
            background: `linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)`,
          }}
        />
      </div>

      {/* Border glow effect - responsive for mobile/desktop */}
      <div
        className={`absolute ${
          // Mobile: horizontal line at bottom/top
          isLeft
            ? "bottom-0 left-0 right-0 h-1 sm:h-auto sm:bottom-auto sm:left-auto sm:right-0 sm:top-0 sm:w-2"
            : "top-0 left-0 right-0 h-1 sm:h-auto sm:top-auto sm:right-auto sm:left-0 sm:bottom-0 sm:w-2"
        }`}
        style={{
          background:
            window.innerWidth < 640
              ? `linear-gradient(to ${
                  isLeft ? "top" : "bottom"
                }, transparent, rgba(${themeColor.rgb}, 0.8))`
              : `linear-gradient(to ${
                  isLeft ? "right" : "left"
                }, transparent, rgba(${themeColor.rgb}, 0.8))`,
          boxShadow: `0 0 20px rgba(${themeColor.rgb}, 0.6)`,
        }}
      />

      {/* Token info - positioned at edges, away from center */}
      <motion.div
        className={`relative z-10 h-full flex flex-col justify-between p-6 sm:p-8 lg:p-12 ${
          !isLeft ? "sm:text-right items-end" : "items-start"
        }`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay, duration: 0.5 }}
      >
        {/* Top: Symbol */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: delay + 0.1 }}
        >
          <div className="text-5xl sm:text-6xl lg:text-7xl font-black text-white drop-shadow-2xl">
            {token.symbol}
          </div>
        </motion.div>

        {/* Bottom: Name and description */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: delay + 0.2 }}
          className={`max-w-sm ${!isLeft ? "sm:text-right" : ""}`}
        >
          <div className="space-y-1 sm:space-y-3">
            <div>
              <div
                className="text-xs sm:text-sm font-semibold tracking-wider mb-1"
                style={{ color: `rgba(${themeColor.rgb}, 0.9)` }}
              >
                {token.symbol}
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-white">
                {token.name}
              </div>
            </div>
            <div className="text-sm sm:text-base text-white/80 leading-relaxed pb-3 sm:pb-0">
              {token.description}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const VersusScreen: React.FC<VersusScreenProps> = ({
  isVisible,
  onComplete,
}) => {
  const { tournament } = useTournament();
  const playWhooshInSound = useSoundEffect(SOUNDS.WHOOSH_IN);
  const playWhooshOutSound = useSoundEffect(SOUNDS.WHOOSH_OUT);

  // Get finals match tokens
  const finalsMatch = tournament.rounds[0]?.matches[0];
  const tokenA = finalsMatch?.tokenA;
  const tokenB = finalsMatch?.tokenB;

  useEffect(() => {
    if (isVisible) {
      // Stop background music immediately when versus screen appears
      soundManager.stopBackgroundMusic();
      console.log("Versus screen visible - stopped background music");

      // Play whoosh_in sound on entrance at 80% volume
      playWhooshInSound(0.8);

      // Play whoosh_out sound and start battle music before transitioning
      const whooshOutTimer = setTimeout(() => {
        playWhooshOutSound(0.6);
        // Start battle music when whoosh_out plays
        soundManager
          .playBackgroundMusic(SOUNDS.BATTLELOOP, 0.5)
          .catch((err) => {
            console.error("Failed to play battle loop:", err);
          });
      }, 4400); // Play just before onComplete

      // Auto-complete after animation - increased duration for readability
      const completeTimer = setTimeout(() => {
        onComplete();
      }, 5000);

      return () => {
        clearTimeout(whooshOutTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [isVisible, onComplete, playWhooshInSound, playWhooshOutSound]);

  if (!tokenA || !tokenB) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-50 bg-black overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Split screen container - vertical on mobile, horizontal on desktop */}
          <div className="relative w-full h-full flex flex-col sm:flex-row">
            {/* Token A - Top on mobile, Left on desktop */}
            <TokenSide token={tokenA} side="left" />

            {/* Token B - Bottom on mobile, Right on desktop */}
            <TokenSide token={tokenB} side="right" />
          </div>

          {/* VS Overlay - Positioned relative to the full screen */}
          <>
            {/* Center line effect - horizontal on mobile, vertical on desktop */}
            <motion.div
              className="fixed inset-0 z-[60] pointer-events-none"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, duration: 0.3 }}
            >
              <div className="w-full h-full flex items-center justify-center">
                {/* Mobile: Horizontal line */}
                <div
                  className="h-px w-full sm:hidden"
                  style={{
                    background:
                      "linear-gradient(to right, transparent, white, transparent)",
                    boxShadow: "0 0 20px rgba(255, 255, 255, 0.5)",
                  }}
                />
                {/* Desktop: Vertical line */}
                <div
                  className="hidden sm:block w-px h-full"
                  style={{
                    background:
                      "linear-gradient(to bottom, transparent, white, transparent)",
                    boxShadow: "0 0 20px rgba(255, 255, 255, 0.5)",
                  }}
                />
              </div>
            </motion.div>

            {/* VS Text - Using fixed positioning */}
            <motion.div
              className="fixed inset-0 z-[70] pointer-events-none"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                delay: 0.4,
                type: "spring",
                stiffness: 100,
                damping: 15,
              }}
            >
              <div className="w-full h-full flex items-center justify-center">
                <motion.div
                  className="text-7xl sm:text-8xl lg:text-9xl font-black text-white"
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  style={{
                    textShadow: `
                          0 0 40px rgba(251, 191, 36, 0.6),
                          0 0 80px rgba(168, 85, 247, 0.4),
                          0 4px 20px rgba(0, 0, 0, 0.5)
                        `,
                  }}
                >
                  VS
                </motion.div>
              </div>
            </motion.div>

            {/* Impact shockwave */}
            <motion.div
              className="fixed inset-0 z-[65] pointer-events-none"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 4, opacity: [0, 1, 0] }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <div className="w-full h-full flex items-center justify-center">
                <div
                  className="w-32 h-32 rounded-full border-2 border-white"
                  style={{
                    boxShadow: "0 0 40px rgba(255, 255, 255, 0.6)",
                  }}
                />
              </div>
            </motion.div>
          </>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
